import { OfflineVisualizatorParser } from "./offline-visualizator-parser";
import { getItemByField } from "../../../../web-graphics";
import { OfflinePlotProvider } from "./offline-plot-provider";
import { OfflineRhythmPlotProvider } from "./offline-rhythm-plot-provider";
import { Plot } from "../../../../web-visualizator";
import { BaseVisualizatorProvider } from "../core/base-visualizator-provider";

export class OfflineVisualizatorProvider extends BaseVisualizatorProvider {
  constructor() {
    super(new OfflineVisualizatorParser());

    this.channelPlots = new Map();
  }

  init(settings, saveableUserSettings) {
    super.init(settings, saveableUserSettings);

    this.addParserCallback(
      "beforeObjectsCreation",
      null,
      function (parser) {
        let canvasCreator = parser.visualizatorCreator.canvasCreator;
        canvasCreator.addCallback(
          "beforeAddItems",
          function (canvasObject) {
            this.canvasObject = canvasObject;
          }.bind(this)
        );
        let plotCreator = canvasCreator.viewObjectCreators.get("plot:offline:base");
        plotCreator?.addCallback("afterCreate", this.afterAddItemAction.bind(this));
        plotCreator = canvasCreator.viewObjectCreators.get("plot:offline:rhythm");
        plotCreator?.addCallback("afterCreate", this.afterAddItemAction.bind(this));
      }.bind(this)
    );
  }

  afterAddItemAction(item, viewObjectSettings /*, creator*/) {
    if (viewObjectSettings === undefined) {
      return;
    }

    let plotInfo = undefined;
    let plotControlsSettings = Plot.findContentSettings(item.settings, "plotControls", "custom");
    const linkedChannelDataSource =
      plotControlsSettings.linkedChannel.id_dataSource ??
      (viewObjectSettings.dataSources.length > 0 ? viewObjectSettings.dataSources[0].id : undefined);
    item.settings._linkedChannel = {
      id_dataSource: linkedChannelDataSource,
      id:
        plotControlsSettings.linkedChannel.id ??
        (linkedChannelDataSource === undefined || linkedChannelDataSource.channels.length == 0
          ? undefined
          : linkedChannelDataSource.channels[0].id)
    };
    if (viewObjectSettings.type == "plot:offline:base") {
      plotInfo = new OfflinePlotProvider(viewObjectSettings._id, viewObjectSettings.type, item);
    } else if (viewObjectSettings.type == "plot:offline:rhythm") {
      plotInfo = new OfflineRhythmPlotProvider(viewObjectSettings._id, viewObjectSettings.type, item);
    }
    plotInfo.init();
    plotInfo.onViewSettingsChanged = this.onViewSettingsChanged;
    plotInfo.viewSettings.width = item.settings.sizeInPixels.width;
    plotInfo.viewSettings.x = item.settings.absoluteLocation.x;
    plotInfo.viewSettings.y =
      item.settings.canvas.settings.sizeInPixels.height - item.settings.absoluteLocation.y - item.settings.sizeInPixels.height;

    plotInfo.viewSettings.controlsHeight = plotControlsSettings.height;
    plotInfo.viewSettings.controlsMargin = plotControlsSettings.margin;
    plotInfo.viewSettings.height = plotControlsSettings.height;

    item.setOnSelectChannel((dataSourceId, channelId) => {
      plotInfo.selectChannel(dataSourceId, channelId);
    });
    plotInfo.saveableUserSettings = this.saveableUserSettings;
    this.plotsInfo.push(plotInfo);
    for (let dataSourceIndex = 0; dataSourceIndex < viewObjectSettings.dataSources.length; dataSourceIndex++) {
      let dataSourceSettings = viewObjectSettings.dataSources[dataSourceIndex];
      let dataSourcesMappedInfo = this.dataSourcesMap.get(dataSourceSettings.id);
      for (let channelIndex = 0; channelIndex < dataSourceSettings.channels.length; channelIndex++) {
        const plotChannelSettings = dataSourceSettings.channels[channelIndex];
        const channelWidth = item.getSignalsPointCount(dataSourceSettings.id, plotChannelSettings.id);
        this._addPlotIdToChannelMappedInfo(dataSourcesMappedInfo, plotChannelSettings, viewObjectSettings._id, plotId => {
          return {
            id: plotId,
            channelWidth: channelWidth
          };
        });
        for (let markIndex = 0; markIndex < dataSourceSettings.marks.length; markIndex++) {
          const plotMarkSettings = dataSourceSettings.marks[markIndex];
          this._addPlotIdToMarkMappedInfo(dataSourcesMappedInfo, plotMarkSettings, viewObjectSettings._id);
        }
        const channelMappedInfo = dataSourcesMappedInfo.channels.get(plotChannelSettings.id);
        if (
          channelMappedInfo !== undefined &&
          (channelMappedInfo.channelMaxWidth === undefined || channelWidth > channelMappedInfo.channelMaxWidth)
        ) {
          channelMappedInfo.channelMaxWidth = channelWidth;
        }
      }
    }
    item.onSignalLabelClicked(plotInfo.viewSettings.linkedChannel.id_dataSource, plotInfo.viewSettings.linkedChannel.id);
  }

  setOnSizeChanged(action) {
    for (let plotIndex = 0; plotIndex < this.plotsInfo.length; plotIndex++) {
      const webglPlot = this.plotsInfo[plotIndex].webglPlot;
      webglPlot.setOnBufferSizeChanged(() => {
        webglPlot.settings.dataSources.forEach(dataSourceSettings => {
          dataSourceSettings.channels.forEach(channelSettings => {
            this._updateChannelInfo(dataSourceSettings.id, channelSettings.id);
          });
        });
        action();
      });
    }
  }

  setMeanderUnit(sourceId, channelName, unit) {
    let dataSourceId = sourceId ?? 0;
    let channelId = this.getChannelId(dataSourceId, channelName);
    this.forEachChannelPlot(
      dataSourceId,
      channelId,
      function (plotInfo) {
        plotInfo.setMeanderUnit(unit);
      }.bind(this)
    );
  }

  addPoints(sourceId, channelId, signalIndex, points) {
    const dataSourceId = sourceId ?? 0;
    const dataSourcesMappedInfo = this.dataSourcesMap.get(dataSourceId);
    const channelInfo = dataSourcesMappedInfo?.channels?.get(channelId);

    channelInfo?.plots?.forEach(channelPlotInfo => {
      const plotInfo = getItemByField("id", channelPlotInfo.id, this.plotsInfo);
      plotInfo.addSignalPoints(sourceId, channelId, signalIndex, points);
    });
  }

  setChannelFrequency(sourceId, channelId, freq) {
    const dataSourceId = sourceId ?? 0;
    super.setChannelFrequency(dataSourceId, channelId, freq);
    this._updateChannelInfo(dataSourceId, channelId);
  }

  _updateChannelInfo(dataSourceId, channelId) {
    const dataSourcesMappedInfo = this.dataSourcesMap.get(dataSourceId);
    const channelInfo = dataSourcesMappedInfo.channels.get(channelId);
    channelInfo.channelMaxWidth = 0;
    channelInfo.plots.forEach(channelPlotInfo => {
      const plotInfo = getItemByField("id", channelPlotInfo.id, this.plotsInfo);
      channelPlotInfo.channelWidth = plotInfo.webglPlot.getSignalsPointCount(dataSourceId, channelId);
      if (channelPlotInfo.channelWidth > channelInfo.channelMaxWidth) {
        channelInfo.channelMaxWidth = channelPlotInfo.channelWidth;
      }
    });
  }
}
